
// import { createSlice, PayloadAction } from '@reduxjs/toolkit';
// import type { Contact, ContactState } from '@/types/contact';

// const initialState: ContactState = {
//   contacts: [],
//   loading: false,
//   error: null,
//   selectedContactId: null,
//   currentContact: null,
//   selectedContacts: [],
//   filters: {
//     search: '',
//     tags: [],
//     status: null
//   },
//   pagination: {
//     page: 1,
//     limit: 10,
//     total: 0
//   },
//   sort: {
//     field: 'name',
//     direction: 'asc'
//   }
// };

// const contactSlice = createSlice({
//   name: 'contacts',
//   initialState,
//   reducers: {
//     setContacts(state, action: PayloadAction<Contact[]>) {
//       state.contacts = action.payload;
//     },
//     addContact(state, action: PayloadAction<Contact>) {
//       state.contacts.push(action.payload);
//     },
//     updateContact(state, action: PayloadAction<Contact>) {
//       const index = state.contacts.findIndex(contact => contact.id === action.payload.id);
//       if (index !== -1) {
//         state.contacts[index] = action.payload;
//       }
//     },
//     deleteContact(state, action: PayloadAction<string>) {
//       state.contacts = state.contacts.filter(contact => contact.id !== action.payload);
//     },
//     setSelectedContactId(state, action: PayloadAction<string | null>) {
//       state.selectedContactId = action.payload;
//     },
//     setCurrentContact(state, action: PayloadAction<Contact | null>) {
//       state.currentContact = action.payload;
//     },
//     setLoading(state, action: PayloadAction<boolean>) {
//       state.loading = action.payload;
//     },
//     setError(state, action: PayloadAction<string | null>) {
//       state.error = action.payload;
//     },
//     selectContact(state, action: PayloadAction<string>) {
//       if (!state.selectedContacts.includes(action.payload)) {
//         state.selectedContacts.push(action.payload);
//       }
//     },
//     unselectContact(state, action: PayloadAction<string>) {
//       state.selectedContacts = state.selectedContacts.filter(id => id !== action.payload);
//     },
//     clearSelectedContacts(state) {
//       state.selectedContacts = [];
//     },
//     setSearchFilter(state, action: PayloadAction<string>) {
//       state.filters.search = action.payload;
//     },
//     setTagsFilter(state, action: PayloadAction<string[]>) {
//       state.filters.tags = action.payload;
//     },
//     setStatusFilter(state, action: PayloadAction<string | null>) {
//       state.filters.status = action.payload;
//     },
//     setPage(state, action: PayloadAction<number>) {
//       state.pagination.page = action.payload;
//     },
//     setLimit(state, action: PayloadAction<number>) {
//       state.pagination.limit = action.payload;
//     },
//     setTotal(state, action: PayloadAction<number>) {
//       state.pagination.total = action.payload;
//     },
//     setSortField(state, action: PayloadAction<string>) {
//       state.sort.field = action.payload;
//     },
//     setSortDirection(state, action: PayloadAction<'asc' | 'desc'>) {
//       state.sort.direction = action.payload;
//     }
//   }
// });

// export const {
//   setContacts,
//   addContact,
//   updateContact,
//   deleteContact,
//   setSelectedContactId,
//   setCurrentContact,
//   setLoading,
//   setError,
//   selectContact,
//   unselectContact,
//   clearSelectedContacts,
//   setSearchFilter,
//   setTagsFilter,
//   setStatusFilter,
//   setPage,
//   setLimit,
//   setTotal,
//   setSortField,
//   setSortDirection
// } = contactSlice.actions;

// export default contactSlice.reducer;


import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { Contact } from '@/types/contact';
import { CreateCustomerData, customerService } from '@/api/services/customerService';

interface ContactsState {
  contacts: Contact[];
  contactDetails: Contact | null;
  loading: boolean;
  error: string | null;
  selectedContacts: string[];
  lastFetchTime: number | null;
}

const initialState: ContactsState = {
  contacts: [],
  contactDetails: null,
  loading: false,
  error: null,
  selectedContacts: [],
  lastFetchTime: null,
};

// ✅ Fetch customers from API
export const fetchCustomers = createAsyncThunk(
  'customers/fetchCustomers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await customerService.fetchCustomers();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch customers');
    }
  }
);

// ✅ Get customer details
export const fetchCustomerDetails = createAsyncThunk(
  'customers/fetchCustomerDetails',
  async (customer_id: string, { rejectWithValue }) => {
    try {
      const customer = await customerService.getCustomerDetails(customer_id);
      return customer.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch customer details');
    }
  }
);

// ✅ Create a new customer
export const createCustomer = createAsyncThunk(
  'customers/createCustomer',
  async (customerData: Partial<Contact> & { workspace_id: string }, { rejectWithValue }) => {
    try {
      // Ensure required fields are strictly strings
      const formattedCustomerData: CreateCustomerData = {
        firstname: String(customerData.firstname || ''),
        lastname: String(customerData.lastname || ''),
        email: String(customerData.email || ''),
        workspace_id: customerData.workspace_id,
        type: customerData.type ? String(customerData.type) : undefined,
        phone: customerData.phone ? String(customerData.phone) : undefined,
        phoneCountry: customerData.phoneCountry ? String(customerData.phoneCountry) : undefined,
        companyId: customerData.companyId ? String(customerData.companyId) : undefined,
      };

      const newCustomer = await customerService.createCustomer(formattedCustomerData);
      return newCustomer;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create customer');
    }
  }
);

// ✅ Update a customer
export const updateCustomer = createAsyncThunk(
  'customers/updateCustomer',
  async (customerData: Partial<Contact> & { customer_id: string }, { dispatch, rejectWithValue }) => {
    try {
      // Extract customer_id from the payload to avoid sending it as part of the data
      const { customer_id, ...dataToUpdate } = customerData;

      const updatedCustomer = await customerService.updateCustomer(customer_id, dataToUpdate);
      dispatch(fetchCustomers());
      return updatedCustomer.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update customer');
    }
  }
);


// ✅ Import customers from CSV
export const importCustomers = createAsyncThunk(
  'customers/importCustomers',
  async (csvFile: File, { rejectWithValue }) => {
    try {
      await customerService.importCustomers(csvFile);
      return 'Customers imported successfully';
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to import customers');
    }
  }
);
// ✅ Add a new contact
export const addContact = createAsyncThunk(
  'customers/addContact',
  async (contactData: Partial<Contact>, { dispatch, rejectWithValue }) => {
    try {
      // Format contact data to match CreateCustomerData requirements
      const formattedContactData: CreateCustomerData = {
        firstname: String(contactData.firstname || ''),
        lastname: String(contactData.lastname || ''),
        email: String(contactData.email || ''),
        workspace_id: String(contactData.workspace_id || ''),
        type: contactData.type ? String(contactData.type) : undefined,
        phone: contactData.phone ? String(contactData.phone) : undefined,
        phoneCountry: contactData.phoneCountry ? String(contactData.phoneCountry) : undefined,
        companyId: contactData.companyId ? String(contactData.companyId) : undefined,
      };
      const newContact = await customerService.createCustomer(formattedContactData);
      dispatch(fetchCustomers());
      return newContact.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to add contact');
    }
  }
);


// ✅ Delete a customer
export const deleteContact = createAsyncThunk(
  'customers/deleteContact',
  async (customerId: string, { dispatch, rejectWithValue }) => {
    try {
      await customerService.deleteContact(customerId);
      dispatch(fetchCustomers());
      return customerId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete contact');
    }
  }
);

const customerSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    // setSelectedContacts: (state, action) => {
    //   state.selectedContacts = action.payload;
    setSelectedContacts: (state, action) => {
      state.selectedContacts = action.payload;
    },
    toggleContactSelection: (state, action) => {
      const contactId = action.payload;
      const index = state.selectedContacts.indexOf(contactId);
      if (index === -1) {
        state.selectedContacts.push(contactId);
      } else {
        state.selectedContacts.splice(index, 1);
      }
    },
    updateContact: (state, action) => {
      const { id, ...updates } = action.payload;
      const contact = state.contacts.find((c) => c.id === id);
      if (contact) {
        Object.assign(contact, updates);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.contacts = action.payload;
        state.lastFetchTime = Date.now();
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchCustomerDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.contactDetails = action.payload;
      })
      .addCase(fetchCustomerDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(importCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(importCustomers.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(importCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateCustomer.pending, (state) => {
        state.error = null;
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        if (state.contactDetails && action.payload) {
          const apiResponse = action.payload;

          // Create a complete merged object
          state.contactDetails = {
            ...state.contactDetails,
            ...apiResponse,
            // Set default values for null fields
            status: (apiResponse.status || state.contactDetails.status || 'active') as 'active' | 'inactive',
            language: apiResponse.language || state.contactDetails.language || 'English',
            // Map API response fields to UI fields
            linkedinUrl: apiResponse.linkedin || state.contactDetails.linkedinUrl,
            twitterUrl: apiResponse.twitter || state.contactDetails.twitterUrl,
            company: apiResponse.companies?.name || apiResponse.company || state.contactDetails.company
          };
        }
      })
      .addCase(updateCustomer.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(addContact.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addContact.fulfilled, (state, action) => {
        state.loading = false;
        state.contacts = [...state.contacts, action.payload];
      })
      .addCase(addContact.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});


export const { setSelectedContacts, toggleContactSelection, updateContact } = customerSlice.actions;
export default customerSlice.reducer;
